/**
 * Point-drawn bubble geometry — turns a handful of rough clicks into a clean,
 * organic outline, and helps place a straight tail against that outline.
 * Coordinate space is caller-defined (used both in 0..1 normalized space and
 * in the 0..100 SVG box space); every function here is unit-agnostic.
 */
export interface Point { x: number; y: number }

/**
 * Catmull-Rom → cubic Bézier for a CLOSED loop of points. This is the
 * "arredondamos pra ele": 3+ rough points become a smooth, liquid outline
 * that always passes through every point given. `tension` controls how
 * tightly the curve hugs the points (lower = rounder/looser).
 */
export function smoothClosedPath(points: Point[], tension = 0.85): string {
  const n = points.length;
  if (n === 0) return '';
  if (n === 1) return `M ${points[0].x} ${points[0].y} Z`;
  if (n === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y} Z`;

  const at = (i: number): Point => points[((i % n) + n) % n];
  const k = tension / 6;
  let d = `M ${at(0).x} ${at(0).y} `;
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1x = p1.x + (p2.x - p0.x) * k;
    const c1y = p1.y + (p2.y - p0.y) * k;
    const c2x = p2.x - (p3.x - p1.x) * k;
    const c2y = p2.y - (p3.y - p1.y) * k;
    d += `C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y} `;
  }
  return `${d}Z`;
}

function cubicBezierPoint(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

const dist = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Resamples a closed loop to `n` evenly-spaced points ALONG ITS SMOOTHED
 * outline (flattens the Catmull-Rom curve, walks it by arc length). Handy to
 * normalize point density (e.g. before offering the user editable handles on
 * an already-smoothed shape) without changing its silhouette.
 */
/** Linear perimeter resampling of a closed polygon to EXACTLY `n` points.
 * Used to give every panel's veil hole the SAME path structure, so CSS can
 * morph (`transition: d`) smoothly from one panel to the next in the reader. */
export function resamplePolygonLinear(points: Point[], n: number): Point[] {
  if (points.length < 3 || n <= points.length) return points;
  // CORNERS ARE SACRED: every original vertex is kept exactly (a chamfered
  // frame would read as "cut wrong" in the guided reader) — the extra points
  // are distributed along the edges proportionally to their length, always
  // summing to exactly `n` so CSS `d` morphing keeps a fixed structure.
  const k = points.length;
  const lens: number[] = [];
  let perimeter = 0;
  for (let i = 0; i < k; i++) {
    const a = points[i], b = points[(i + 1) % k];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    lens.push(len);
    perimeter += len;
  }
  if (perimeter <= 0) return points;
  const extraTotal = n - k;
  // Largest-remainder allocation of extra points per edge.
  const exact = lens.map((l) => (l / perimeter) * extraTotal);
  const alloc = exact.map(Math.floor);
  let leftover = extraTotal - alloc.reduce((a, b) => a + b, 0);
  const order = exact
    .map((e, i) => ({ frac: e - Math.floor(e), i }))
    .sort((a, b) => b.frac - a.frac);
  for (let j = 0; j < order.length && leftover > 0; j++, leftover--) alloc[order[j].i]++;
  const out: Point[] = [];
  for (let i = 0; i < k; i++) {
    const a = points[i], b = points[(i + 1) % k];
    out.push({ x: a.x, y: a.y }); // the vertex itself, exact
    const m = alloc[i];
    for (let j = 1; j <= m; j++) {
      const t = j / (m + 1);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out;
}

export function resampleLoop(points: Point[], n: number, tension = 0.85): Point[] {
  if (points.length < 3 || n < 3) return points;
  const STEPS = 12;
  const flat: Point[] = [];
  const len = points.length;
  const at = (i: number): Point => points[((i % len) + len) % len];
  const k = tension / 6;
  for (let i = 0; i < len; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1 = { x: p1.x + (p2.x - p0.x) * k, y: p1.y + (p2.y - p0.y) * k };
    const c2 = { x: p2.x - (p3.x - p1.x) * k, y: p2.y - (p3.y - p1.y) * k };
    for (let s = 0; s < STEPS; s++) flat.push(cubicBezierPoint(p1, c1, c2, p2, s / STEPS));
  }
  const cum: number[] = [0];
  for (let i = 1; i < flat.length; i++) cum.push(cum[i - 1] + dist(flat[i - 1], flat[i]));
  const total = cum[cum.length - 1] || 1;
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const target = (total * i) / n;
    let idx = cum.findIndex((c) => c >= target);
    if (idx <= 0) idx = 1;
    const segLen = cum[idx] - cum[idx - 1] || 1;
    const t = (target - cum[idx - 1]) / segLen;
    const a = flat[idx - 1], b = flat[idx % flat.length];
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out;
}

/** Arithmetic-mean centroid — good enough for placing a tail's emission point
 * on a hand-drawn (possibly irregular) body. */
export function centroidOf(points: Point[]): Point {
  let x = 0, y = 0;
  for (const p of points) { x += p.x; y += p.y; }
  return { x: x / points.length, y: y / points.length };
}

/**
 * Finds where a ray from `origin` (assumed inside the shape, e.g. its
 * centroid) toward `angle` (radians) exits a closed polygon. Used to anchor a
 * speech-bubble tail against the real edge of an arbitrary hand-drawn body —
 * the same role `ellipse` boundary math plays for the classic oval bubbles.
 * Falls back to a point far along the ray if the polygon has no clean exit
 * (degenerate/self-intersecting input).
 */
export function rayPolygonExit(origin: Point, angle: number, polygon: Point[]): Point {
  const dx = Math.cos(angle), dy = Math.sin(angle);
  const n = polygon.length;
  let best: { t: number; x: number; y: number } | null = null;
  for (let i = 0; i < n; i++) {
    const a = polygon[i], b = polygon[(i + 1) % n];
    const ex = b.x - a.x, ey = b.y - a.y;
    const det = ex * dy - ey * dx;
    if (Math.abs(det) < 1e-9) continue; // parallel to this edge
    const rx = a.x - origin.x, ry = a.y - origin.y;
    const t = (ex * ry - ey * rx) / det; // distance along the ray
    const s = (dx * ry - dy * rx) / det; // position along the edge (0..1)
    if (s >= -1e-6 && s <= 1 + 1e-6 && t > 1e-6 && (!best || t < best.t)) {
      best = { t, x: origin.x + dx * t, y: origin.y + dy * t };
    }
  }
  return best ? { x: best.x, y: best.y } : { x: origin.x + dx * 30, y: origin.y + dy * 30 };
}
